import styled from "styled-components";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { formatMoney } from "../utils/formatMoney";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";

const CardContainer = styled.label<{
  $selected: boolean;
  $selectable: boolean;
  $readOnly: boolean;
  $disabled: boolean;
}>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: ${spacing.x3}px;
  border: 2px solid
    ${({ $selected, $readOnly, $disabled }) =>
      $readOnly || $disabled
        ? colors.border
        : $selected
          ? colors.primary
          : colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${({ $selected, $readOnly, $disabled }) =>
    $readOnly || $disabled
      ? colors.backgroundDark
      : $selected
        ? colors.infoBackground
        : colors.background};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  cursor: ${({ $selectable }) => ($selectable ? "pointer" : "default")};
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: ${({ $selectable, $selected, $readOnly, $disabled }) =>
      $readOnly || $disabled
        ? colors.border
        : $selectable
          ? $selected
            ? colors.primary
            : colors.textMuted
          : colors.border};
  }
`;

const RadioInput = styled.input.attrs({ type: "radio" })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

const RadioIndicator = styled.div<{ $selected: boolean; $disabled: boolean }>`
  position: absolute;
  top: ${spacing.x2}px;
  right: ${spacing.x2}px;
  width: 20px;
  height: 20px;
  border: 2px solid
    ${({ $selected, $disabled }) =>
      $selected
        ? $disabled
          ? colors.textMuted
          : colors.primary
        : colors.border};
  border-radius: 50%;
  background-color: ${({ $selected, $disabled }) =>
    $selected
      ? $disabled
        ? colors.textMuted
        : colors.primary
      : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${colors.primaryContrastText};
    display: ${({ $selected }) => ($selected ? "block" : "none")};
  }
`;

const CheckboxIndicator = styled.div<{ $selected: boolean; $disabled: boolean }>`
  position: absolute;
  top: ${spacing.x2}px;
  right: ${spacing.x2}px;
  width: 20px;
  height: 20px;
  border: 2px solid
    ${({ $selected, $disabled }) =>
      $selected
        ? $disabled
          ? colors.textMuted
          : colors.primary
        : colors.border};
  border-radius: ${borderRadius.small}px;
  background-color: ${({ $selected, $disabled }) =>
    $selected
      ? $disabled
        ? colors.textMuted
        : colors.primary
      : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "✓";
    color: ${colors.primaryContrastText};
    font-size: ${fontSizes.small}px;
    font-weight: bold;
    display: ${({ $selected }) => ($selected ? "block" : "none")};
  }
`;

const CardContent = styled.div<{ $readOnly: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  min-width: 0;
  padding-right: ${({ $readOnly }) => ($readOnly ? 0 : spacing.x4)}px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const CardTitle = styled.h3`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  min-width: 0;
  word-break: break-word;
`;

const CardDescription = styled.p`
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  margin: 0;
  line-height: 1.5;
`;

const FeaturesList = styled.ul`
  margin: ${spacing.x1}px 0 0 0;
  padding-left: ${spacing.x3}px;
  list-style-type: disc;
`;

const FeatureTooltip = styled.div`
  position: absolute;
  left: 0;
  bottom: calc(100% + ${spacing.x1}px);
  width: max-content;
  max-width: 280px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  background-color: ${colors.text};
  color: ${colors.background};
  font-size: ${fontSizes.small}px;
  line-height: 1.4;
  border-radius: ${borderRadius.small}px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  z-index: 20;
  pointer-events: none;
`;

const FeatureItemContainer = styled.li`
  position: relative;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin: ${spacing.x05}px 0;

  &:hover > ${FeatureTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

const FeatureLabel = styled.span<{ $hasTooltip: boolean }>`
  cursor: ${({ $hasTooltip }) => ($hasTooltip ? "help" : "inherit")};
  border-bottom: ${({ $hasTooltip }) =>
    $hasTooltip ? `1px dotted ${colors.textMuted}` : "none"};
`;

const PriceContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px ${spacing.x2}px;
  margin-top: ${spacing.x1}px;
  padding-top: ${spacing.x1}px;
  border-top: 1px solid ${colors.border};
`;

const PriceValue = styled.span`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
  min-width: 0;
  word-break: break-word;
`;

const PriceUnit = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  font-weight: normal;
`;

const ComingSoonBadge = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.warningText};
  background-color: ${colors.warningBackground};
  padding: ${spacing.x05}px ${spacing.x1}px;
  border-radius: 4px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
`;

export type PackageCardFeature =
  | string
  | {
      label: string;
      tooltip?: string;
    };

interface PackageCardProps {
  id: string;
  name: string;
  description: string;
  features?: PackageCardFeature[];
  price?: { monthlyPrice?: MultiCurrencyMoney; prepaid36MonthPrice?: MultiCurrencyMoney };
  selected: boolean;
  onSelect: (selected: boolean) => void;
  selectionType: "radio" | "checkbox";
  disabled?: boolean;
  comingSoon?: boolean;
  readOnly?: boolean;
}

export const PackageCard = ({
  id,
  name,
  description,
  features,
  price,
  selected,
  onSelect,
  selectionType,
  disabled = false,
  comingSoon = false,
  readOnly = false,
}: PackageCardProps) => {
  const handleChange = () => {
    if (!disabled && !comingSoon && !readOnly) {
      if (selectionType === "radio" && !selected) {
        onSelect(true);
      } else if (selectionType === "checkbox") {
        onSelect(!selected);
      }
    }
  };

  const isDisabled = disabled || comingSoon || readOnly;

  return (
    <CardContainer
      $selected={selected}
      $selectable={!isDisabled}
      $readOnly={readOnly}
      $disabled={disabled || comingSoon}
    >
      {!readOnly &&
        (selectionType === "radio" ? (
          <>
            <RadioInput
              type="radio"
              name={id}
              checked={selected}
              onChange={handleChange}
              disabled={isDisabled}
            />
            <RadioIndicator
              $selected={selected}
              $disabled={disabled || comingSoon}
            />
          </>
        ) : (
          <>
            <CheckboxInput
              type="checkbox"
              checked={selected}
              onChange={handleChange}
              disabled={isDisabled}
            />
            <CheckboxIndicator
              $selected={selected}
              $disabled={disabled || comingSoon}
            />
          </>
        ))}
      <CardContent $readOnly={readOnly}>
        <TitleRow>
          <CardTitle>{name}</CardTitle>
          {comingSoon && <ComingSoonBadge>Kommer snart</ComingSoonBadge>}
        </TitleRow>
        <CardDescription>{description}</CardDescription>
        {features && features.length > 0 && (
          <FeaturesList>
            {features.map((feature) => {
              const label = typeof feature === "string" ? feature : feature.label;
              const tooltip =
                typeof feature === "string" ? undefined : feature.tooltip;

              return (
                <FeatureItemContainer key={label}>
                  <FeatureLabel $hasTooltip={!!tooltip}>
                    {label}
                  </FeatureLabel>
                  {tooltip && <FeatureTooltip>{tooltip}</FeatureTooltip>}
                </FeatureItemContainer>
              );
            })}
          </FeaturesList>
        )}
        {price && (
          <PriceContainer>
            {price.monthlyPrice && price.monthlyPrice.currencies?.[Currency.SEK] &&
              <PriceValue>
                {formatMoney(price.monthlyPrice.currencies?.[Currency.SEK])}
                <PriceUnit>/mån</PriceUnit>
              </PriceValue>
            }
            {price.prepaid36MonthPrice && price.prepaid36MonthPrice.currencies?.[Currency.SEK] && (
              <PriceValue>
                {formatMoney(price.prepaid36MonthPrice.currencies?.[Currency.SEK])}
                <PriceUnit>/mån (36+ mån)</PriceUnit>
              </PriceValue>
            )}
          </PriceContainer>
        )}
      </CardContent>
    </CardContainer>
  );
};
